/** 经营总览：品牌/省份/销售/客户排行 */

import { buildCompositionChartRows } from '../../../charts/composition/lib/compositionChartBuckets';
import { COMPOSITION_CHART_DIMENSION, COMPOSITION_CHART_METRIC } from '../../../charts/composition/lib/compositionChartConfig';
import { ORDER_PRIMARY_PHASE_DIMENSIONS, resolveOrderDimensionKey, resolveOrderLineItemDimensionBuckets } from '../../../charts/pipeline/lib/orderPrimaryPhaseDimension';
import { QUARTER_AXIS, sumSliceTotalPrice } from './overviewMetricsFormat';

/** KPI 品牌/省份图例色：蓝 / 绿 / 橙区分度更高（避免 primary+info 同冷色难辨） */
export const OVERVIEW_RANK_COLOR_TYPES = ['primary', 'success', 'warning', 'info', 'secondary'];

const appendOtherSlice = (slices, tail, getThemeColor) => {
	if (!tail.length) return slices;
	const otherPrice = tail.reduce((sum, row) => sum + (Number(row.totalPrice) || 0), 0);
	const otherCount = tail.reduce((sum, row) => sum + (Number(row.count) || 0), 0);
	if (!(otherPrice > 0 || otherCount > 0)) return slices;
	slices.push({
		name: '其他',
		color: getThemeColor('secondary'),
		count: otherCount,
		totalPrice: otherPrice,
	});
	return slices;
};

/**
 * 经营总览 KPI：按品牌/省份聚合合同金额 TopN（口径同组成图）。
 * legend 展示前 legendN；chart/bubble 含「其他」收口（bubble 默认前 4 + 其他）。
 */

/**
 * 经营总览 KPI：按品牌/省份聚合合同金额 TopN（口径同组成图）。
 * legend 展示前 legendN；chart/bubble 含「其他」收口（bubble 默认前 4 + 其他）。
 */
export const buildOverviewRankSlicesFromRows = (rankedRows = [], getThemeColor, { legendN = 3, chartN = 3, bubbleN = 4 } = {}) => {
	const ranked = (rankedRows ?? []).filter((row) => (Number(row.totalPrice) || 0) > 0 || (Number(row.count) || 0) > 0);
	const toSlice = (row, index) => ({
		name: row.label || row.name || '未分类',
		color: getThemeColor(OVERVIEW_RANK_COLOR_TYPES[index % OVERVIEW_RANK_COLOR_TYPES.length]),
		count: Number(row.count) || 0,
		totalPrice: Number(row.totalPrice) || 0,
	});

	const legendSlices = ranked.slice(0, legendN).map(toSlice);
	const chartSlices = appendOtherSlice(ranked.slice(0, chartN).map(toSlice), ranked.slice(chartN), getThemeColor);
	const bubbleSlices = appendOtherSlice(ranked.slice(0, bubbleN).map(toSlice), ranked.slice(bubbleN), getThemeColor);

	return {
		legendSlices,
		chartSlices,
		bubbleSlices,
		totalPrice: sumSliceTotalPrice(ranked.map(toSlice)),
	};
};

/**
 * 经营总览 KPI：按品牌/省份聚合合同金额 TopN（口径同组成图）。
 * legend 展示前 legendN；chart/bubble 含「其他」收口（bubble 默认前 4 + 其他）。
 */

/**
 * 经营总览 KPI：按品牌/省份聚合合同金额 TopN（口径同组成图）。
 * legend 展示前 legendN；chart/bubble 含「其他」收口（bubble 默认前 4 + 其他）。
 */
export const buildOverviewRankSlices = (orders = [], dimension, getThemeColor, { legendN = 3, chartN = 3, bubbleN = 4 } = {}) => {
	const { items, totalMetric } = buildCompositionChartRows({
		orders,
		dimension,
		metric: COMPOSITION_CHART_METRIC.TOTAL_PRICE,
	});
	const result = buildOverviewRankSlicesFromRows(items, getThemeColor, { legendN, chartN, bubbleN });
	return {
		...result,
		totalPrice: Number(totalMetric) || result.totalPrice,
	};
};

export const buildOverviewBrandSlices = (orders, getThemeColor, options) => buildOverviewRankSlices(orders, COMPOSITION_CHART_DIMENSION.BRAND, getThemeColor, options);

export const buildOverviewProvinceSlices = (orders, getThemeColor, options) => buildOverviewRankSlices(orders, COMPOSITION_CHART_DIMENSION.PROVINCE, getThemeColor, options);

/** 优先用服务端 chartAggregates；无聚合时回退订单扫表 */

/** 优先用服务端 chartAggregates；无聚合时回退订单扫表 */
export const buildOverviewBrandSlicesFromStats = (stats, getThemeColor, options = {}) => {
	const rows = stats?.chartAggregates?.dimensions?.brand;
	if (Array.isArray(rows) && rows.length > 0) {
		return buildOverviewRankSlicesFromRows(rows, getThemeColor, options);
	}
	return buildOverviewBrandSlices(stats?.orders, getThemeColor, options);
};

export const buildOverviewProvinceSlicesFromStats = (stats, getThemeColor, options = {}) => {
	const rows = stats?.chartAggregates?.dimensions?.province;
	if (Array.isArray(rows) && rows.length > 0) {
		return buildOverviewRankSlicesFromRows(rows, getThemeColor, options);
	}
	return buildOverviewProvinceSlices(stats?.orders, getThemeColor, options);
};

/** 近 7 个自然日（含锚点日）本地日键；不随顶栏日期筛裁剪 */
const buildLast7DayKeys = (anchor = new Date()) => {
	const base = anchor instanceof Date && !Number.isNaN(anchor.getTime()) ? anchor : new Date();
	return Array.from({ length: 7 }, (_, i) => {
		const d = new Date(base.getFullYear(), base.getMonth(), base.getDate() - (6 - i));
		return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
	});
};

const padDatePart = (n) => String(n).padStart(2, '0');

/** 近 7 日订单区间（YYYY-MM-DD），供销售 spark 独立查询 */

/** 近 7 日订单区间（YYYY-MM-DD），供销售 spark 独立查询 */
export const buildLast7DayDateRange = (anchor = new Date()) => {
	const base = anchor instanceof Date && !Number.isNaN(anchor.getTime()) ? anchor : new Date();
	const start = new Date(base.getFullYear(), base.getMonth(), base.getDate() - 6);
	return {
		startDate: `${start.getFullYear()}-${padDatePart(start.getMonth() + 1)}-${padDatePart(start.getDate())}`,
		endDate: `${base.getFullYear()}-${padDatePart(base.getMonth() + 1)}-${padDatePart(base.getDate())}`,
	};
};

const toLocalDayKey = (value) => {
	const d = value ? new Date(value) : null;
	if (!d || Number.isNaN(d.getTime())) return null;
	return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
};

/** 销售排行：按销售员聚合合同额 / 单数 / 完成率；近 7 日 spark 可用 weekSparkOrders 固定口径 */

/** 销售排行：按销售员聚合合同额 / 单数 / 完成率；近 7 日 spark 可用 weekSparkOrders 固定口径 */
export const buildOverviewSalesRankRows = (orders = [], { limit = 6, now = new Date(), weekSparkOrders = null } = {}) => {
	const { items } = buildCompositionChartRows({
		orders,
		dimension: COMPOSITION_CHART_DIMENSION.SALE_USER,
		metric: COMPOSITION_CHART_METRIC.TOTAL_PRICE,
	});
	const completedByKey = new Map();
	const ordersBySale = new Map();
	const weekOrdersBySale = new Map();
	const todayKey = toLocalDayKey(now);
	const sparkSource = weekSparkOrders == null ? orders : weekSparkOrders;

	for (const order of orders ?? []) {
		const key = resolveOrderDimensionKey(order, ORDER_PRIMARY_PHASE_DIMENSIONS.SALE_USER);
		if (order?.isCompleted) completedByKey.set(key, (completedByKey.get(key) || 0) + 1);
		if (!ordersBySale.has(key)) ordersBySale.set(key, []);
		ordersBySale.get(key).push(order);
	}

	for (const order of sparkSource ?? []) {
		const key = resolveOrderDimensionKey(order, ORDER_PRIMARY_PHASE_DIMENSIONS.SALE_USER);
		if (!weekOrdersBySale.has(key)) weekOrdersBySale.set(key, []);
		weekOrdersBySale.get(key).push(order);
	}

	const buildWeekSparkForOrders = (saleWeekOrders = []) => {
		const weekDayKeys = buildLast7DayKeys(now);
		const weekIndex = new Map(weekDayKeys.map((k, i) => [k, i]));
		const spark = Array.from({ length: 7 }, () => 0);
		for (const order of saleWeekOrders) {
			const dayKey = toLocalDayKey(order?.createdAt);
			const dayIdx = dayKey == null ? undefined : weekIndex.get(dayKey);
			if (dayIdx == null) continue;
			spark[dayIdx] += 1;
		}
		return spark;
	};

	const countToday = (saleOrders = []) => {
		let n = 0;
		for (const order of saleOrders) {
			if (toLocalDayKey(order?.createdAt) === todayKey) n += 1;
		}
		return n;
	};

	return (items ?? [])
		.filter((row) => (Number(row.totalPrice) || 0) > 0 || (Number(row.count) || 0) > 0)
		.slice(0, limit)
		.map((row) => {
			const count = Number(row.count) || 0;
			const completed = completedByKey.get(row.key) || 0;
			const saleOrders = ordersBySale.get(row.key) || [];
			const saleWeekOrders = weekOrdersBySale.get(row.key) || [];
			return {
				key: row.key,
				name: row.label || '未分配',
				totalPrice: Number(row.totalPrice) || 0,
				count,
				todayCount: countToday(saleOrders),
				completed,
				completionRate: count > 0 ? completed / count : 0,
				weekSpark: buildWeekSparkForOrders(saleWeekOrders),
			};
		});
};

const EMPTY_WEEK_SPARK = [0, 0, 0, 0, 0, 0, 0];

const normalizeWeekSpark = (values) => {
	const raw = (values ?? []).map((n) => Number(n) || 0);
	while (raw.length < 7) raw.unshift(0);
	return raw.slice(-7);
};

/** 销售排行：优先 chartAggregates.saleUser（含 completedCount / weekSpark / todayCount） */

/** 销售排行：优先 chartAggregates.saleUser（含 completedCount / weekSpark / todayCount） */
export const buildOverviewSalesRankRowsFromStats = (stats, { limit = 6 } = {}) => {
	const rows = stats?.chartAggregates?.dimensions?.saleUser;
	if (Array.isArray(rows) && rows.length > 0) {
		return rows
			.filter((row) => (Number(row.totalPrice) || 0) > 0 || (Number(row.count) || 0) > 0)
			.slice(0, limit)
			.map((row) => {
				const count = Number(row.count) || 0;
				const completed = Number(row.completedCount) || 0;
				return {
					key: row.key,
					name: row.label || '未分配',
					totalPrice: Number(row.totalPrice) || 0,
					count,
					todayCount: Number(row.todayCount) || 0,
					completed,
					completionRate: count > 0 ? completed / count : 0,
					weekSpark: Array.isArray(row.weekSpark) ? normalizeWeekSpark(row.weekSpark) : EMPTY_WEEK_SPARK,
				};
			});
	}
	return buildOverviewSalesRankRows(stats?.orders, { limit });
};

const visitOrderBrandKeys = (order, onEachKey) => {
	const seen = new Set();
	for (const line of resolveOrderLineItemDimensionBuckets(order, ORDER_PRIMARY_PHASE_DIMENSIONS.BRAND)) {
		if (seen.has(line.key)) continue;
		seen.add(line.key);
		onEachKey(line.key);
	}
};

/** 品牌排行：订单含该品牌计 1 单；完成/当天/近7日同口径 */

/** 品牌排行：订单含该品牌计 1 单；完成/当天/近7日同口径 */
export const buildOverviewBrandRankRows = (orders = [], { limit = 6, now = new Date(), weekSparkOrders = null, filters = {} } = {}) => {
	const { items } = buildCompositionChartRows({
		orders,
		dimension: COMPOSITION_CHART_DIMENSION.BRAND,
		metric: COMPOSITION_CHART_METRIC.TOTAL_PRICE,
		filters,
	});
	const completedByKey = new Map();
	const ordersByBrand = new Map();
	const weekOrdersByBrand = new Map();
	const todayKey = toLocalDayKey(now);
	const sparkSource = weekSparkOrders == null ? orders : weekSparkOrders;

	for (const order of orders ?? []) {
		visitOrderBrandKeys(order, (key) => {
			if (order?.isCompleted) completedByKey.set(key, (completedByKey.get(key) || 0) + 1);
			if (!ordersByBrand.has(key)) ordersByBrand.set(key, []);
			ordersByBrand.get(key).push(order);
		});
	}

	for (const order of sparkSource ?? []) {
		visitOrderBrandKeys(order, (key) => {
			if (!weekOrdersByBrand.has(key)) weekOrdersByBrand.set(key, []);
			weekOrdersByBrand.get(key).push(order);
		});
	}

	const buildWeekSparkForOrders = (brandWeekOrders = []) => {
		const weekDayKeys = buildLast7DayKeys(now);
		const weekIndex = new Map(weekDayKeys.map((k, i) => [k, i]));
		const spark = Array.from({ length: 7 }, () => 0);
		for (const order of brandWeekOrders) {
			const dayKey = toLocalDayKey(order?.createdAt);
			const dayIdx = dayKey == null ? undefined : weekIndex.get(dayKey);
			if (dayIdx == null) continue;
			spark[dayIdx] += 1;
		}
		return spark;
	};

	const countToday = (brandOrders = []) => {
		let n = 0;
		for (const order of brandOrders) {
			if (toLocalDayKey(order?.createdAt) === todayKey) n += 1;
		}
		return n;
	};

	return (items ?? [])
		.filter((row) => (Number(row.totalPrice) || 0) > 0 || (Number(row.count) || 0) > 0)
		.slice(0, limit)
		.map((row) => {
			const count = Number(row.count) || 0;
			const completed = completedByKey.get(row.key) || 0;
			const brandOrders = ordersByBrand.get(row.key) || [];
			const brandWeekOrders = weekOrdersByBrand.get(row.key) || [];
			return {
				key: row.key,
				name: row.label || '未分类',
				totalPrice: Number(row.totalPrice) || 0,
				count,
				todayCount: countToday(brandOrders),
				completed,
				completionRate: count > 0 ? completed / count : 0,
				weekSpark: buildWeekSparkForOrders(brandWeekOrders),
			};
		});
};

/** 品牌排行：优先 chartAggregates.brand（含 completedCount / weekSpark / todayCount） */

/** 品牌排行：优先 chartAggregates.brand（含 completedCount / weekSpark / todayCount） */
export const buildOverviewBrandRankRowsFromStats = (stats, { limit = 6, filters = {} } = {}) => {
	const rows = stats?.chartAggregates?.dimensions?.brand;
	if (Array.isArray(rows) && rows.length > 0) {
		return rows
			.filter((row) => (Number(row.totalPrice) || 0) > 0 || (Number(row.count) || 0) > 0)
			.slice(0, limit)
			.map((row) => {
				const count = Number(row.count) || 0;
				const completed = Number(row.completedCount) || 0;
				return {
					key: row.key,
					name: row.label || '未分类',
					totalPrice: Number(row.totalPrice) || 0,
					count,
					todayCount: Number(row.todayCount) || 0,
					completed,
					completionRate: count > 0 ? completed / count : 0,
					weekSpark: Array.isArray(row.weekSpark) ? normalizeWeekSpark(row.weekSpark) : EMPTY_WEEK_SPARK,
				};
			});
	}
	return buildOverviewBrandRankRows(stats?.orders, { limit, filters });
};

const parseLocalDate = (value) => {
	if (!value) return null;
	if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
	if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
		const [y, m, d] = value.slice(0, 10).split('-').map(Number);
		return new Date(y, m - 1, d);
	}
	const d = new Date(value);
	return Number.isNaN(d.getTime()) ? null : d;
};

/**
 * 客户 Top spark 桶：
 * - 年：月柱，当年裁到本月（如 2026→7 根）
 * - 月：该月全日柱（28–31）
 * - 季：区间内完整季各一根（单季=1 根；跨多季=多根）
 */

/**
 * 客户 Top spark 桶：
 * - 年：月柱，当年裁到本月（如 2026→7 根）
 * - 月：该月全日柱（28–31）
 * - 季：区间内完整季各一根（单季=1 根；跨多季=多根）
 */
export const buildCustomerTopSparkBuckets = ({ periodUnit = 'year', startDate = null, endDate = null, now = new Date() } = {}) => {
	const anchor = now instanceof Date && !Number.isNaN(now.getTime()) ? now : new Date();
	const start = parseLocalDate(startDate);
	const end = parseLocalDate(endDate);

	if (periodUnit === 'month') {
		const y = start?.getFullYear() ?? anchor.getFullYear();
		const m = start?.getMonth() ?? anchor.getMonth();
		const daysInMonth = new Date(y, m + 1, 0).getDate();
		return Array.from({ length: daysInMonth }, (_, index) => {
			const day = index + 1;
			return {
				key: `${y}-${m + 1}-${day}`,
				label: `${m + 1}/${day}`,
			};
		});
	}

	if (periodUnit === 'quarter') {
		const rangeStart = start ?? new Date(anchor.getFullYear(), Math.floor(anchor.getMonth() / 3) * 3, 1);
		const rangeEnd = end ?? new Date(anchor.getFullYear(), Math.floor(anchor.getMonth() / 3) * 3 + 3, 0);
		const startQ = rangeStart.getFullYear() * 4 + Math.floor(rangeStart.getMonth() / 3);
		const endQ = rangeEnd.getFullYear() * 4 + Math.floor(rangeEnd.getMonth() / 3);
		const from = Math.min(startQ, endQ);
		const to = Math.max(startQ, endQ);
		const spanYears = Math.floor(from / 4) !== Math.floor(to / 4);
		const buckets = [];
		for (let cursor = from; cursor <= to; cursor += 1) {
			const y = Math.floor(cursor / 4);
			const q = cursor % 4;
			buckets.push({
				key: `${y}-Q${q + 1}`,
				label: spanYears ? `${y} ${QUARTER_AXIS[q]}` : QUARTER_AXIS[q],
			});
		}
		return buckets;
	}

	/** 年：自区间起点月（默认 1 月）至「今天」或区间终点月 */
	const y = start?.getFullYear() ?? anchor.getFullYear();
	const firstMonth = start?.getFullYear() === y ? start.getMonth() : 0;
	let lastMonth = 11;
	if (y === anchor.getFullYear()) {
		lastMonth = anchor.getMonth();
	} else if (end && end.getFullYear() === y) {
		lastMonth = end.getMonth();
	}
	lastMonth = Math.max(firstMonth, Math.min(11, lastMonth));

	const buckets = [];
	for (let m = firstMonth; m <= lastMonth; m += 1) {
		buckets.push({
			key: `${y}-${m + 1}`,
			label: `${m + 1}月`,
		});
	}
	return buckets;
};

const orderSparkBucketKey = (order, periodUnit) => {
	const d = parseLocalDate(order?.createdAt);
	if (!d) return null;
	if (periodUnit === 'month') return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
	if (periodUnit === 'quarter') return `${d.getFullYear()}-Q${Math.floor(d.getMonth() / 3) + 1}`;
	return `${d.getFullYear()}-${d.getMonth() + 1}`;
};

/** 服务端 sparkByDay（`YYYY-M-D`）映射到年/月/季桶 */

/** 服务端 sparkByDay（`YYYY-M-D`）映射到年/月/季桶 */
const dayKeyToPeriodBucketKey = (dayKey, periodUnit) => {
	if (!dayKey || typeof dayKey !== 'string') return null;
	const parts = dayKey.split('-').map(Number);
	if (parts.length < 3 || parts.some((n) => !Number.isFinite(n))) return null;
	const [y, m, d] = parts;
	if (periodUnit === 'month') return `${y}-${m}-${d}`;
	if (periodUnit === 'quarter') return `${y}-Q${Math.floor((m - 1) / 3) + 1}`;
	return `${y}-${m}`;
};

const buildSparkFromDayMap = (sparkByDay = {}, buckets = [], periodUnit = 'year') => {
	const spark = Array.from({ length: buckets.length }, () => 0);
	const sparkCounts = Array.from({ length: buckets.length }, () => 0);
	const bucketIndex = new Map(buckets.map((b, i) => [b.key, i]));
	for (const [dayKey, cell] of Object.entries(sparkByDay ?? {})) {
		const key = dayKeyToPeriodBucketKey(dayKey, periodUnit);
		const idx = key == null ? undefined : bucketIndex.get(key);
		if (idx == null) continue;
		spark[idx] += Number(cell?.totalPrice) || 0;
		sparkCounts[idx] += Number(cell?.count) || 0;
	}
	return { spark, sparkCounts };
};

/** 销售卡底部：客户采购量 Top3 + 按年/月/季动态柱数 spark */

/** 销售卡底部：客户采购量 Top3 + 按年/月/季动态柱数 spark */
export const buildOverviewCustomerTopRows = (
	orders = [],
	{ limit = 3, periodUnit = 'year', startDate = null, endDate = null, now = new Date() } = {},
) => {
	const { items } = buildCompositionChartRows({
		orders,
		dimension: COMPOSITION_CHART_DIMENSION.CUSTOMER,
		metric: COMPOSITION_CHART_METRIC.TOTAL_PRICE,
	});
	const buckets = buildCustomerTopSparkBuckets({ periodUnit, startDate, endDate, now });
	const bucketIndex = new Map(buckets.map((b, i) => [b.key, i]));
	const ordersByCustomer = new Map();

	for (const order of orders ?? []) {
		const key = resolveOrderDimensionKey(order, ORDER_PRIMARY_PHASE_DIMENSIONS.CUSTOMER);
		if (!ordersByCustomer.has(key)) ordersByCustomer.set(key, []);
		ordersByCustomer.get(key).push(order);
	}

	const buildSpark = (customerOrders = []) => {
		const spark = Array.from({ length: buckets.length }, () => 0);
		const sparkCounts = Array.from({ length: buckets.length }, () => 0);
		for (const order of customerOrders) {
			const key = orderSparkBucketKey(order, periodUnit);
			const idx = key == null ? undefined : bucketIndex.get(key);
			if (idx == null) continue;
			spark[idx] += Number(order?.totalPrice) || 0;
			sparkCounts[idx] += 1;
		}
		return { spark, sparkCounts };
	};

	return (items ?? [])
		.filter((row) => (Number(row.totalPrice) || 0) > 0 || (Number(row.count) || 0) > 0)
		.slice(0, limit)
		.map((row) => {
			const customerOrders = ordersByCustomer.get(row.key) || [];
			const { spark, sparkCounts } = buildSpark(customerOrders);
			return {
				key: row.key,
				name: row.label || '未分类',
				totalPrice: Number(row.totalPrice) || 0,
				count: Number(row.count) || 0,
				spark,
				sparkCounts,
				sparkLabels: buckets.map((b) => b.label),
			};
		});
};

/** 客户 Top：优先 chartAggregates（含 TopN sparkByDay）；无按日桶时再空柱降级 */

/** 客户 Top：优先 chartAggregates（含 TopN sparkByDay）；无按日桶时再空柱降级 */
export const buildOverviewCustomerTopRowsFromStats = (
	stats,
	{ limit = 3, periodUnit = 'year', startDate = null, endDate = null, now = new Date() } = {},
) => {
	const rows = stats?.chartAggregates?.dimensions?.customer;
	if (Array.isArray(rows) && rows.length > 0) {
		const buckets = buildCustomerTopSparkBuckets({ periodUnit, startDate, endDate, now });
		const emptySpark = Array.from({ length: buckets.length }, () => 0);
		return rows
			.filter((row) => (Number(row.totalPrice) || 0) > 0 || (Number(row.count) || 0) > 0)
			.slice(0, limit)
			.map((row) => {
				const hasDaySpark = row?.sparkByDay && typeof row.sparkByDay === 'object' && Object.keys(row.sparkByDay).length > 0;
				const { spark, sparkCounts } = hasDaySpark ? buildSparkFromDayMap(row.sparkByDay, buckets, periodUnit) : { spark: emptySpark, sparkCounts: emptySpark };
				return {
					key: row.key,
					name: row.label || '未分类',
					totalPrice: Number(row.totalPrice) || 0,
					count: Number(row.count) || 0,
					spark,
					sparkCounts,
					sparkLabels: buckets.map((b) => b.label),
				};
			});
	}
	return buildOverviewCustomerTopRows(stats?.orders, {
		limit,
		periodUnit,
		startDate,
		endDate,
		now,
	});
};

/** 应收排行：客户 / 销售 */
